import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Shield, Upload, Trash2, Download, AlertCircle, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LiabilityInsuranceTab = ({ centerName = "", organization = {}, onComplianceChange }) => {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [inlineDates, setInlineDates] = useState({});
  const [savingDates, setSavingDates] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchDocs(); }, []);

  const fetchDocs = async () => {
    try {
      const res = await axios.get(`${API}/liability-insurance`);
      setDocs(res.data || []);
    } catch {}
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (!['application/pdf','image/jpeg','image/png','image/jpg','application/msword','application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(file.type)) {
      toast.error("Please upload a PDF, DOC, DOCX, or image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) { toast.error("File must be under 10MB"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('centerName', centerName || organization?.name || '');
      const res = await axios.post(`${API}/liability-insurance/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDocs(prev => [res.data, ...prev]);
      toast.success("Document uploaded");
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (onComplianceChange) onComplianceChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  }, [uploading]);

  const handleDelete = async (docId) => {
    try {
      await axios.delete(`${API}/liability-insurance/${docId}`);
      setDocs(prev => prev.filter(d => d.id !== docId));
      toast.success("Document deleted");
      if (onComplianceChange) onComplianceChange();
    } catch { toast.error("Failed to delete"); }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/liability-insurance/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = doc.fileName || 'insurance.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const startEdit = (doc) => {
    setEditingId(doc.id);
    setEditValues({
      displayName: doc.displayName || doc.fileName || "",
      insuranceProvider: doc.insuranceProvider || "",
      policyNumber: doc.policyNumber || "",
      effectiveDate: doc.effectiveDate || "",
      expirationDate: doc.expirationDate || "",
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditValues({}); };

  // Inline date entry — same pattern as Occupancy Permit
  const needsDates = (doc) => !doc.effectiveDate || !doc.expirationDate || isExpired(doc.expirationDate);

  const startInlineDates = (doc) => {
    setInlineDates(prev => ({
      ...prev,
      [doc.id]: {
        effectiveDate: doc.effectiveDate || "",
        expirationDate: isExpired(doc.expirationDate) ? "" : (doc.expirationDate || "")
      }
    }));
  };

  const saveDates = async (docId) => {
    const vals = inlineDates[docId] || {};
    if (!vals.effectiveDate || !vals.expirationDate) {
      toast.error("Both effective and expiration dates are required");
      return;
    }
    setSavingDates(docId);
    try {
      const doc = docs.find(d => d.id === docId);
      await axios.patch(`${API}/liability-insurance/${docId}/rename`, {
        displayName: doc.displayName || doc.fileName,
        effectiveDate: vals.effectiveDate,
        expirationDate: vals.expirationDate
      });
      setDocs(prev => prev.map(d =>
        d.id === docId ? { ...d, effectiveDate: vals.effectiveDate, expirationDate: vals.expirationDate } : d
      ));
      setInlineDates(prev => { const n = { ...prev }; delete n[docId]; return n; });
      toast.success("Dates saved!");
      if (onComplianceChange) onComplianceChange();
    } catch { toast.error("Failed to save dates"); }
    finally { setSavingDates(null); }
  };

  const saveEdit = async (docId) => {
    if (!editValues.displayName?.trim()) { toast.error("Name cannot be empty"); return; }
    try {
      await axios.patch(`${API}/liability-insurance/${docId}/rename`, editValues);
      setDocs(prev => prev.map(d => d.id === docId ? { ...d, ...editValues } : d));
      toast.success("Details saved");
      cancelEdit();
      if (onComplianceChange) onComplianceChange();
    } catch { toast.error("Failed to save"); }
  };

  const isExpired = (d) => d && new Date(d) < new Date();
  const isExpiringSoon = (d) => {
    if (!d) return false;
    const days = Math.floor((new Date(d) - new Date()) / 86400000);
    return days >= 0 && days <= 30;
  };

  // Robust formatter: date-only strings (YYYY-MM-DD) are treated as calendar
  // dates using local components so they never shift by a day due to UTC
  // parsing. Full ISO timestamps, Date objects, null/blank, and malformed
  // legacy values are all handled without crashing or showing "Invalid Date".
  const formatDate = (d) => {
    if (!d) return null;
    if (d instanceof Date) {
      return isNaN(d.getTime()) ? null : d.toLocaleDateString();
    }
    if (typeof d !== 'string') return d;
    const dateOnlyMatch = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, y, m, day] = dateOnlyMatch;
      const parsed = new Date(Number(y), Number(m) - 1, Number(day));
      return isNaN(parsed.getTime()) ? d : parsed.toLocaleDateString();
    }
    const parsed = new Date(d);
    return isNaN(parsed.getTime()) ? d : parsed.toLocaleDateString();
  };

  const handlePrint = () => {
    const printContent = docs.map(doc => {
      const name = doc.displayName || doc.fileName || "Insurance Document";
      const expired = isExpired(doc.expirationDate);
      return `
        <div style="border:1px solid #ccc; border-radius:8px; padding:16px; margin-bottom:16px; page-break-inside:avoid;">
          <div style="font-size:15px; font-weight:700; color:#1e3a5f; margin-bottom:8px;">${name}</div>
          <table style="width:100%; font-size:13px; border-collapse:collapse;">
            ${doc.insuranceProvider ? `<tr><td style="color:#666;padding:2px 8px 2px 0;width:140px;">Insurance Provider</td><td style="font-weight:600;">${doc.insuranceProvider}</td></tr>` : ''}
            ${doc.policyNumber ? `<tr><td style="color:#666;padding:2px 8px 2px 0;">Policy Number</td><td style="font-weight:600;">${doc.policyNumber}</td></tr>` : ''}
            ${doc.effectiveDate ? `<tr><td style="color:#666;padding:2px 8px 2px 0;">Effective Date</td><td>${formatDate(doc.effectiveDate)}</td></tr>` : ''}
            ${doc.expirationDate ? `<tr><td style="color:#666;padding:2px 8px 2px 0;">Expiration Date</td><td style="color:${expired ? '#dc2626' : '#166534'};font-weight:600;">${formatDate(doc.expirationDate)}${expired ? ' — EXPIRED' : ''}</td></tr>` : ''}
            ${doc.fileName ? `<tr><td style="color:#666;padding:2px 8px 2px 0;">File Name</td><td>${doc.fileName}</td></tr>` : ''}
          </table>
        </div>`;
    }).join('');

    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>Liability Insurance Documents</title>
      <style>body{font-family:Arial,sans-serif;padding:32px;max-width:800px;margin:0 auto;}
      h1{color:#1e3a5f;font-size:20px;margin-bottom:4px;}
      p{color:#666;font-size:13px;margin-bottom:24px;}
      @media print{button{display:none!important;}}</style></head>
      <body>
        <h1>Liability Insurance Documents</h1>
        <p>Printed: ${new Date().toLocaleString()} &nbsp;·&nbsp; Total: ${docs.length} document${docs.length !== 1 ? 's' : ''}</p>
        <button onclick="window.print()" style="margin-bottom:20px;padding:8px 16px;background:#1e3a5f;color:white;border:none;border-radius:6px;cursor:pointer;font-size:13px;">Print / Save PDF</button>
        ${printContent}
      </body></html>`);
    win.document.close();
  };

  return (
    <div className="space-y-4">

      {/* Dark Blue Header — always present */}
      <div className="bg-blue-900 rounded-xl px-5 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-blue-200" />
            <span className="text-white font-semibold text-base">Uploaded Insurance Documents</span>
            <span className="bg-blue-700 text-blue-100 text-sm font-bold px-2.5 py-0.5 rounded-full">{docs.length}</span>
          </div>
          {docs.length > 0 && (
            <Button
              size="sm"
              onClick={handlePrint}
              className="bg-blue-700 hover:bg-blue-600 text-white border-0 text-xs h-8"
              data-testid="print-insurance-btn"
            >
              Print Details
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <AlertCircle className="w-3.5 h-3.5 text-yellow-300 flex-shrink-0" />
          <span className="text-yellow-200 text-xs font-bold tracking-wide">
            POLICY PERIOD REQUIRED — use the Add Dates button on each document
          </span>
        </div>
      </div>

      {/* Document Rows */}
      {docs.length > 0 && (
        <div className="space-y-2">
          {docs.map((doc) => {
            const isEditing = editingId === doc.id;
            const expired = isExpired(doc.expirationDate);
            const expiring = isExpiringSoon(doc.expirationDate);
            const displayName = doc.displayName || doc.fileName || "Insurance Document";

            return (
              <div
                key={doc.id}
                className={`rounded-lg border px-4 py-3 ${expired ? 'bg-red-50 border-red-200' : expiring ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`}
                data-testid={`ins-doc-${doc.id}`}
              >
                {/* Date required warning — replaced by inline form below */}
                {isEditing ? (
                  /* Edit layout — row 1: name/provider/policy, row 2: dates + save/cancel */
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Input
                        value={editValues.displayName}
                        onChange={e => setEditValues(p => ({ ...p, displayName: e.target.value }))}
                        placeholder="Document name"
                        className="h-7 text-xs w-48"
                        autoFocus
                      />
                      <Input
                        value={editValues.insuranceProvider}
                        onChange={e => setEditValues(p => ({ ...p, insuranceProvider: e.target.value }))}
                        placeholder="Provider"
                        className="h-7 text-xs w-32"
                      />
                      <Input
                        value={editValues.policyNumber}
                        onChange={e => setEditValues(p => ({ ...p, policyNumber: e.target.value }))}
                        placeholder="Policy #"
                        className="h-7 text-xs w-28"
                      />
                      <Input
                        value={editValues.policyPhone}
                        onChange={e => setEditValues(p => ({ ...p, policyPhone: e.target.value }))}
                        placeholder="Policy Phone #"
                        className="h-7 text-xs w-32"
                        type="tel"
                      />
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-medium text-gray-600 whitespace-nowrap">Effective Date:</span>
                        <Input
                          type="date"
                          value={editValues.effectiveDate}
                          onChange={e => setEditValues(p => ({ ...p, effectiveDate: e.target.value }))}
                          className="h-7 text-xs w-36"
                        />
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-medium text-gray-600 whitespace-nowrap">Expiration Date:</span>
                        <Input
                          type="date"
                          value={editValues.expirationDate}
                          onChange={e => setEditValues(p => ({ ...p, expirationDate: e.target.value }))}
                          className="h-7 text-xs w-36"
                        />
                      </div>
                      <button onClick={() => saveEdit(doc.id)} className="text-green-600 hover:text-green-700">
                        <Check className="w-4 h-4" />
                      </button>
                      <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-600">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Normal row */
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Line 1: name + provider + policy + pencil */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <Shield className={`w-4 h-4 flex-shrink-0 ${expired ? 'text-red-500' : expiring ? 'text-amber-500' : 'text-blue-700'}`} />
                        <span className="text-sm font-semibold text-gray-900 truncate">{displayName}</span>
                        {doc.insuranceProvider && <span className="text-xs text-gray-500">· {doc.insuranceProvider}</span>}
                        {doc.policyNumber && <span className="text-xs text-gray-400">#{doc.policyNumber}</span>}
                        {doc.policyPhone && (
                          <span className="text-xs text-blue-600 font-medium">📞 {doc.policyPhone}</span>
                        )}
                        <button
                          onClick={() => startEdit(doc)}
                          className="text-gray-300 hover:text-blue-600 transition-colors flex-shrink-0"
                          title="Edit details"
                          data-testid={`edit-btn-${doc.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {/* Line 2: dates */}
                      {(doc.effectiveDate || doc.expirationDate) && (
                        <div className="flex items-center gap-4 mt-1 ml-6 text-xs">
                          {doc.effectiveDate && (
                            <span className="text-gray-500">
                              <span className="font-medium text-gray-600">Effective Date:</span> {formatDate(doc.effectiveDate)}
                            </span>
                          )}
                          {doc.expirationDate && (
                            <span className={expired ? 'text-red-600 font-medium' : expiring ? 'text-amber-600 font-medium' : 'text-gray-500'}>
                              {expired && <AlertCircle className="w-3 h-3 inline mr-0.5" />}
                              <span className="font-medium">Expiration Date:</span> {formatDate(doc.expirationDate)}{expired ? ' (EXPIRED)' : expiring ? ' (Expiring Soon)' : ''}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      <Button size="sm" variant="outline" onClick={() => handleDownload(doc)} className="h-7 px-2.5 text-xs text-blue-700 border-blue-300 hover:bg-blue-50">
                        <Download className="w-3 h-3 mr-1" />Download
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(doc.id)} className="h-7 w-7 p-0 text-red-500 border-red-200 hover:bg-red-50" data-testid={`delete-btn-${doc.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Inline date entry for docs missing dates or expired */}
                {!isEditing && needsDates(doc) && (
                  <div className={`mt-3 pt-3 border-t ${isExpired(doc.expirationDate) ? 'border-red-200' : 'border-amber-200'}`}>
                    {inlineDates[doc.id] !== undefined ? (
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Effective Date</label>
                          <Input
                            type="date"
                            value={inlineDates[doc.id].effectiveDate}
                            onChange={e => setInlineDates(prev => ({ ...prev, [doc.id]: { ...prev[doc.id], effectiveDate: e.target.value } }))}
                            className="mt-1 h-8 text-sm w-44"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Expiration Date</label>
                          <Input
                            type="date"
                            value={inlineDates[doc.id].expirationDate}
                            onChange={e => setInlineDates(prev => ({ ...prev, [doc.id]: { ...prev[doc.id], expirationDate: e.target.value } }))}
                            className="mt-1 h-8 text-sm w-44"
                          />
                        </div>
                        <div className="flex gap-2 pb-1">
                          <Button
                            size="sm"
                            onClick={() => saveDates(doc.id)}
                            disabled={savingDates === doc.id}
                            className="h-8 bg-blue-700 hover:bg-blue-800 text-white"
                          >
                            {savingDates === doc.id ? "Saving..." : "Save Dates"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setInlineDates(prev => { const n = { ...prev }; delete n[doc.id]; return n; })}
                            className="h-8"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium flex items-center gap-1.5 ${isExpired(doc.expirationDate) ? 'text-red-700' : 'text-amber-700'}`}>
                          <AlertCircle className="w-4 h-4" />
                          {isExpired(doc.expirationDate)
                            ? 'Policy expired — update dates for compliance'
                            : 'Policy dates required for compliance'}
                        </span>
                        <Button
                          size="sm"
                          onClick={() => startInlineDates(doc)}
                          className={`h-7 text-white text-xs ${isExpired(doc.expirationDate) ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'}`}
                        >
                          {isExpired(doc.expirationDate) ? 'Renew Policy' : 'Add Dates'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Drag & Drop Upload Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => !uploading && fileInputRef.current?.click()}
        data-testid="liability-drop-zone"
        className={`cursor-pointer rounded-xl border-2 border-dashed transition-all duration-200 p-8 text-center select-none
          ${dragOver ? 'border-blue-500 bg-blue-50 scale-[1.01]' : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/40'}
          ${uploading ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          onChange={e => uploadFile(e.target.files?.[0])}
          disabled={uploading}
          className="hidden"
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <div className="w-7 h-7 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-blue-700 text-sm font-medium">Uploading...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${dragOver ? 'bg-blue-200' : 'bg-gray-200'}`}>
              <Upload className={`w-6 h-6 ${dragOver ? 'text-blue-700' : 'text-gray-500'}`} />
            </div>
            <p className="font-medium text-gray-700">{dragOver ? 'Drop file here' : 'Drag & drop or click to upload'}</p>
            <p className="text-xs text-gray-400">PDF, DOC, DOCX, JPG, PNG — max 10MB</p>
          </div>
        )}
      </div>

    </div>
  );
};

export default LiabilityInsuranceTab;
